import AirdropsMetadata from "files/airdrops.json";
import { contractConfig } from "@config/contractsConfig";
import {
  getElMerkleProof,
  getElMerkleProofList,
  getScrollMerkleProofList,
} from "@services/index";
import { wagmiConfig } from "@services/WagmiProvider";
import { useQuery } from "@tanstack/react-query";
import { readContracts } from "@wagmi/core";

import { Abi, formatEther, Hash } from "viem";
import { mainnet, scroll } from "viem/chains";
import { useAccount, useReadContract, useReadContracts } from "wagmi";

export enum ClaimType {
  SCROLL = "scroll",
  EL = "el",
  ELPK = "elPk",
}

type ClaimConfig = {
  contract: { address: Hash; abi: Abi };
  asset: string;
  unit: string;
  chainId: number;
  fetchMerkleProofList: (address: Hash) => Promise<any>;
  getTotalAmount: (merkleProofList: any) => bigint;
  getProofs: (merkleProofList: any) => any;
  iconPath: string;
};

export type Airdrop = {
  claimedAmount: bigint;
  totalAmount: bigint;
  unit: string;
  availableAmount: bigint;
  currentIndex: bigint;
  asset: string;
  chainId: number;
  type: ClaimType;
  contract: { address: Hash; abi: Abi };
  proofs: any;
  iconPath: string;
  lastUpdatedOn?: string;
};

export type ClaimData = {
  data: Airdrop;
  isLoading: boolean;
  key: ClaimType;
  refetch: () => void;
  isRefetching: boolean;
  error: any;
};

type ClaimConfigs = {
  [key in ClaimType]: ClaimConfig;
};

const configs: ClaimConfigs = {
  [ClaimType.SCROLL]: {
    contract: contractConfig[scroll.id].interactionPools.scrollDistributor,
    chainId: scroll.id,
    asset: "Scroll Token",
    unit: "SCR",
    fetchMerkleProofList: getScrollMerkleProofList,
    getTotalAmount: (merkleProofList) =>
      BigInt(merkleProofList?.data?.marks || 0),
    getProofs: (merkleProofList) => merkleProofList?.data?.proof,
    iconPath: "/assets/chains/scroll.svg",
  },
  [ClaimType.EL]: {
    contract: contractConfig[mainnet.id].interactionPools.elMerkleDistributor,
    chainId: mainnet.id,
    unit: "EIGEN",
    asset: "Eigen - Season 2",
    fetchMerkleProofList: getElMerkleProofList,
    getTotalAmount: (merkleProofList) => BigInt(merkleProofList?.data?.el || 0),
    getProofs: (merkleProofList) => merkleProofList?.data?.proofs,
    iconPath: "/assets/claim/eigen.svg",
  },
  [ClaimType.ELPK]: {
    contract: contractConfig[mainnet.id].interactionPools.elPkMerkleDistributor,
    chainId: mainnet.id,
    unit: "EIGEN",
    asset: "Eigen - Programmatic Rewards",
    fetchMerkleProofList: getElMerkleProofList,
    getTotalAmount: (merkleProofList) =>
      BigInt(merkleProofList?.programmatic?.el || 1),
    getProofs: (merkleProofList) => merkleProofList?.programmatic?.proofs,
    iconPath: "/assets/claim/eigen.svg",
  },
};

const fetchMerkleData = async (
  config: ClaimConfig,
  address: Hash,
  type: ClaimType
) => {
  const [
    { result: currentIndex },
    { result: userClaims },
    // { result: currentMerkleRoot },
  ] = await readContracts(wagmiConfig, {
    contracts: [
      {
        ...config.contract,
        functionName: "currentIndex",
        chainId: config.chainId,
      },
      {
        ...config.contract,
        functionName: "userClaims",
        args: [address],
        chainId: config.chainId,
      },
      //   {
      //     ...config.contract,
      //     functionName: "currentMerkleRoot",
      //     args: [],
      //     chainId: config.chainId,
      //   },
    ],
  });

  const { data: merkleProofList } = await config.fetchMerkleProofList(address);

  const totalAmount = config.getTotalAmount(merkleProofList);
  const claimedAmount = (userClaims as [bigint, bigint])[1] || 0n;
  const availableAmount = totalAmount - claimedAmount;

  const meta: Record<string, any> = AirdropsMetadata?.metadata;
  const lastUpdatedOn: string = meta?.[type]?.last_updated_on || "";

  return {
    claimedAmount,
    totalAmount,
    deadline: "01 Dec 2024",
    lastUpdatedOn: lastUpdatedOn,
    unit: config.unit,
    availableAmount,
    currentIndex: currentIndex as bigint,
    asset: config.asset,
    chainId: config.chainId,
    type,
    contract: config.contract,
    proofs: config.getProofs(merkleProofList),
    iconPath: config.iconPath,
  };
};

const useFetchMerkleData = (type: ClaimType, address?: Hash) => {
  return useQuery({
    queryKey: ["claim", type],
    queryFn: async () => fetchMerkleData(configs[type], address as Hash, type),
    enabled: !!address,
    refetchInterval: 60_000,
  });
};

const useClaims = () => {
  const { address } = useAccount();
  const {
    data: elData,
    isLoading: elDataLoading,
    error: elError,
    refetch: refetchEl,
    isRefetching: elRefetching,
  } = useFetchMerkleData(ClaimType.EL, address);

  const {
    data: elpkData,
    isLoading: elpkDataLoading,
    error: elpkError,
    refetch: refetchElPk,
    isRefetching: elpkRefetching,
  } = useFetchMerkleData(ClaimType.ELPK, address);

  const {
    data: scrollData,
    isLoading: scrollDataLoading,
    error: scrollError,
    refetch: refetchScroll,
    isRefetching: scrollRefetching,
  } = useFetchMerkleData(ClaimType.SCROLL, address);

  if (elError || elpkError || scrollError) {
    console.log({ elError, elpkError, scrollError });
  }

  const isLoading = elDataLoading || elpkDataLoading || scrollDataLoading;

  const data = [
    {
      isLoading: elDataLoading,
      data: elData,
      key: ClaimType.EL,
      refetch: refetchEl,
      isRefetching: elRefetching,
      error: elError,
    },
    {
      isLoading: elpkDataLoading,
      data: elpkData,
      key: ClaimType.ELPK,
      refetch: refetchElPk,
      isRefetching: elpkRefetching,
      error: elpkError,
    },
    {
      isLoading: scrollDataLoading,
      data: scrollData,
      key: ClaimType.SCROLL,
      refetch: refetchScroll,
      isRefetching: scrollRefetching,
      error: scrollError,
    },
  ].filter((d) => !d.error && d.data);

  return { isLoading, data };
};

export default useClaims;
